import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { caseFileAPI, archiveRequestAPI, taskAPI, noteAPI, jobAPI, userAPI, vekaletAPI } from '../api/api';
import { TASK_STATUS, TASK_STATUS_LABELS } from '../utils/constants';
import NoteForm from '../components/Notes/NoteForm';
import JobForm from '../components/Jobs/JobForm';
import TaskForm from '../components/Tasks/TaskForm';
import Navbar from '../components/Layout/Navbar';
import ActionIcon from '../components/Common/ActionIcon';
import { formatDateForDisplay, formatJobDueDateForDisplay, formatDateOnly, formatDateToLocalString, toISODateOrNull, getTodayDateString } from '../utils/dateUtils';
import { isOzelJob } from '../utils/ozelDosyaAccess';
import './Dashboard.css';
import { onayla } from '../utils/onayla';
import BosDurum from '../components/Common/BosDurum';
import { YENI_TASARIM } from '../utils/yeniTasarim';

const addDaysToDateKey = (key, days) => {
  const [y, m, d] = String(key || '').split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
};

const addMonthsToDateKey = (key, months) => {
  const [y, m, d] = String(key || '').split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(Date.UTC(y, m - 1 + months, d));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
};

const Dashboard = () => {
  const { token, user } = useAuth();
  const [stats, setStats] = useState({
    totalFiles: 0,
    activeFiles: 0,
    archivedFiles: 0
  });
  const [showPastFiles, setShowPastFiles] = useState(false);
  const [showPastHearings, setShowPastHearings] = useState(false);
  const [showPastVekalet, setShowPastVekalet] = useState(false);
  const [showPastZamanAsimi, setShowPastZamanAsimi] = useState(false);
  // İşaretlenmesi devam eden (sunucuya kaydediliyor) öğeler için anahtar seti
  const [markingKeys, setMarkingKeys] = useState({});
  // Sunucu henüz erişilebilir değilse (endpoint yayınlanmadıysa) tik'in kaybolmaması
  // için yerel yedek. Sunucuya başarıyla kaydedilince bu geçersiz kılma temizlenir.
  const [localMarks, setLocalMarks] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dashboardDoneMarks') || '{}'); } catch (e) { return {}; }
  });
  const persistLocalMarks = (obj) => {
    try { localStorage.setItem('dashboardDoneMarks', JSON.stringify(obj)); } catch (e) {}
  };
  const [myTasks, setMyTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myNotes, setMyNotes] = useState([]);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [loadingNote, setLoadingNote] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobFormData, setJobFormData] = useState({
    Description: '',
    DueDate: '',
    CaseFileId: null
  });
  const [caseFiles, setCaseFiles] = useState([]);
  const [vekaletler, setVekaletler] = useState([]);
  const [activeTab, setActiveTab] = useState('jobs'); // 'jobs' veya 'tasks'
  const [jobFilter, setJobFilter] = useState('pending'); // 'pending', 'underReview', 'approved', 'rejected', 'all' - ilk açılışta Yapılacaklar
  const [rejectionReason, setRejectionReason] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [completeModalJob, setCompleteModalJob] = useState(null);
  const [completeComment, setCompleteComment] = useState('');
  const [sortColumn, setSortColumn] = useState('dueDate'); // 'foyNo', 'muvekkil', 'description', etc. - varsayılan: son tarih
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc' - varsayılan: en eski/geçmiş tarih üstte
  const [taskSortColumn, setTaskSortColumn] = useState(null);
  const [taskSortDirection, setTaskSortDirection] = useState('asc');
  const [noteSortColumn, setNoteSortColumn] = useState('tarih');
  const [noteSortDirection, setNoteSortDirection] = useState('desc');
  const [taskFilter, setTaskFilter] = useState(String(TASK_STATUS.InProgress)); // İlk açılışta İşlemde; 'all' | '1' | '2' | '3' | '4'
  const [selectedJob, setSelectedJob] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [editingTaskMode, setEditingTaskMode] = useState('complete');
  const [users, setUsers] = useState([]);
  const [loadingTask, setLoadingTask] = useState(false);
  const [now, setNow] = useState(new Date());
  const navigate = useNavigate();
  const arbitrationRef = useRef(null);
  const hearingsRef = useRef(null);
  const DASH_SPLIT_MIN = 30;
  const DASH_SPLIT_MAX = 70;
  const [mainLeftPct, setMainLeftPct] = useState(() => {
    const saved = parseFloat(localStorage.getItem('dashboardMainLeftPct'));
    return Number.isFinite(saved) && saved >= DASH_SPLIT_MIN && saved <= DASH_SPLIT_MAX ? saved : 50;
  });
  const [bottomLeftPct, setBottomLeftPct] = useState(() => {
    const saved = parseFloat(localStorage.getItem('dashboardBottomLeftPct'));
    return Number.isFinite(saved) && saved >= DASH_SPLIT_MIN && saved <= DASH_SPLIT_MAX ? saved : 50;
  });
  const mainSplitRef = useRef(null);
  const bottomSplitRef = useRef(null);
  const mainDraggingRef = useRef(false);
  const bottomDraggingRef = useRef(false);

  useEffect(() => {
    localStorage.setItem('dashboardMainLeftPct', String(mainLeftPct));
  }, [mainLeftPct]);

  useEffect(() => {
    localStorage.setItem('dashboardBottomLeftPct', String(bottomLeftPct));
  }, [bottomLeftPct]);

  useEffect(() => {
    const onMove = (e) => {
      if (mainDraggingRef.current && mainSplitRef.current) {
        const rect = mainSplitRef.current.getBoundingClientRect();
        if (rect.width > 0) {
          let pct = ((e.clientX - rect.left) / rect.width) * 100;
          pct = Math.max(DASH_SPLIT_MIN, Math.min(DASH_SPLIT_MAX, pct));
          setMainLeftPct(pct);
        }
      }
      if (bottomDraggingRef.current && bottomSplitRef.current) {
        const rect = bottomSplitRef.current.getBoundingClientRect();
        if (rect.width > 0) {
          let pct = ((e.clientX - rect.left) / rect.width) * 100;
          pct = Math.max(DASH_SPLIT_MIN, Math.min(DASH_SPLIT_MAX, pct));
          setBottomLeftPct(pct);
        }
      }
    };
    const onUp = () => {
      if (mainDraggingRef.current || bottomDraggingRef.current) {
        mainDraggingRef.current = false;
        bottomDraggingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const beginMainSplit = (e) => {
    mainDraggingRef.current = true;
    e.preventDefault();
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const beginBottomSplit = (e) => {
    bottomDraggingRef.current = true;
    e.preventDefault();
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const JOB_STATUS_LABELS = {
    0: 'Yapılacak',
    1: 'Kontrol Bekliyor',
    2: 'Onaylandı',
    3: 'Reddedildi'
  };
  const getJobStatusLabel = (job) => {
    const isCompleted = job.IsCompleted ?? job.isCompleted ?? false;
    if (!isCompleted) return 'Yapılacak';
    const status = job.Status ?? job.status ?? 0;
    return JOB_STATUS_LABELS[status] ?? 'Bilinmiyor';
  };

  const isAdmin = () => {
    if (!user) return false;
    const role = user.Role ?? user.role ?? '';
    return role === 'Admin';
  };

  // Sıralama fonksiyonu - İş Listesi
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sıralama fonksiyonu - Görevler
  const handleTaskSort = (column) => {
    if (taskSortColumn === column) {
      setTaskSortDirection(taskSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setTaskSortColumn(column);
      setTaskSortDirection('asc');
    }
  };

  const handleTaskUpdate = async (taskData) => {
    setLoadingTask(true);
    try {
      await taskAPI.update(taskData);
      const tasks = await taskAPI.getMyTasks();
      setMyTasks(tasks);
      setEditingTask(null);
      setEditingTaskMode('complete');
      window.dispatchEvent(new CustomEvent('taskUpdated', { detail: { taskId: taskData.TaskId } }));
      if (taskData.Status === TASK_STATUS.Completed) {
        alert('Görev tamamlandı olarak işaretlendi.');
      } else if (taskData.Response) {
        alert('Görev kontrole gönderildi');
      } else {
        alert('Görev güncellendi.');
      }
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoadingTask(false);
    }
  };

  const handleDeleteTask = async (task) => {
    if (!await onayla('Bu görevi silmek istediğinize emin misiniz?')) return;
    setLoadingTask(true);
    try {
      await taskAPI.delete(task.Id ?? task.id);
      const tasks = await taskAPI.getMyTasks();
      setMyTasks(tasks);
      setEditingTask(null);
      window.dispatchEvent(new CustomEvent('taskUpdated', { detail: { taskId: task.Id ?? task.id } }));
      alert('Görev silindi.');
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoadingTask(false);
    }
  };

  // Filtrelenmiş ve sıralanmış işler
  const filteredAndSortedJobs = useMemo(() => {
    // Önce filtreleme yap — özel dosya işleri anasayfada gösterilmez
    let filtered = jobs.filter((job) => !isOzelJob(job));
    
    switch (jobFilter) {
      case 'pending':
        // Yapılacaklar - henüz tamamlanmamış
        filtered = filtered.filter(job => {
          const isCompleted = job.IsCompleted ?? job.isCompleted ?? false;
          return !isCompleted;
        });
        break;
      case 'underReview':
        // Kontrole Gidenler - tamamlandı ama onay bekliyor (status 0 veya 1)
        filtered = filtered.filter(job => {
          const isCompleted = job.IsCompleted ?? job.isCompleted ?? false;
          const status = job.Status ?? job.status ?? 0;
          return isCompleted && (status === 0 || status === 1);
        });
        break;
      case 'approved':
        // Onaylananlar - status 2
        filtered = filtered.filter(job => {
          const status = job.Status ?? job.status ?? 0;
          return status === 2;
        });
        break;
      case 'rejected':
        // Reddedilenler - status 3
        filtered = filtered.filter(job => {
          const status = job.Status ?? job.status ?? 0;
          return status === 3;
        });
        break;
      case 'all':
      default:
        // Tümü - filtreleme yok
        break;
    }
    
    // Sonra sıralama yap
    if (!sortColumn) return filtered;
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      let aValue, bValue;
      switch (sortColumn) {
        case 'foyNo':
          aValue = a.CaseFileFoyNo ?? a.caseFileFoyNo ?? 0;
          bValue = b.CaseFileFoyNo ?? b.caseFileFoyNo ?? 0;
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'muvekkil':
          aValue = (a.CaseFileMuvekkil ?? a.caseFileMuvekkil ?? '').toLowerCase();
          bValue = (b.CaseFileMuvekkil ?? b.caseFileMuvekkil ?? '').toLowerCase();
          return sortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'description':
          aValue = (a.Description ?? a.description ?? '').toLowerCase();
          bValue = (b.Description ?? b.description ?? '').toLowerCase();
          return sortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'createdBy':
          aValue = `${a.CreatedByUserName ?? a.createdByUserName ?? ''} ${a.CreatedByUserSurname ?? a.createdByUserSurname ?? ''}`.trim().toLowerCase();
          bValue = `${b.CreatedByUserName ?? b.createdByUserName ?? ''} ${b.CreatedByUserSurname ?? b.createdByUserSurname ?? ''}`.trim().toLowerCase();
          return sortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'createdDate':
          aValue = new Date(a.CreatedDate ?? a.createdDate ?? 0);
          bValue = new Date(b.CreatedDate ?? b.createdDate ?? 0);
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'dueDate': {
          // Eski tarihten yeniye; tarihi olmayanlar en sonda
          const da = (a.DueDate ?? a.dueDate) ? formatDateToLocalString(a.DueDate ?? a.dueDate) : '';
          const db = (b.DueDate ?? b.dueDate) ? formatDateToLocalString(b.DueDate ?? b.dueDate) : '';
          if (!da && !db) return 0;
          if (!da) return 1;
          if (!db) return -1;
          const cmp = da.localeCompare(db);
          return sortDirection === 'asc' ? cmp : -cmp;
        }
        case 'completedBy':
          aValue = `${a.CompletedByUserName ?? a.completedByUserName ?? ''} ${a.CompletedByUserSurname ?? a.completedByUserSurname ?? ''}`.trim().toLowerCase();
          bValue = `${b.CompletedByUserName ?? b.completedByUserName ?? ''} ${b.CompletedByUserSurname ?? b.completedByUserSurname ?? ''}`.trim().toLowerCase();
          return sortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'completedDate':
          aValue = a.CompletedDate ?? a.completedDate ? new Date(a.CompletedDate ?? a.completedDate) : new Date(0);
          bValue = b.CompletedDate ?? b.completedDate ? new Date(b.CompletedDate ?? b.completedDate) : new Date(0);
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        default:
          return 0;
      }
    });
    return sorted;
  }, [jobs, sortColumn, sortDirection, jobFilter]);

  // Filtrelenmiş görevler (Bana Atanan Görevler sekmeleri)
  const filteredTasksForDashboard = useMemo(() => {
    if (taskFilter === 'all') return myTasks;
    const statusNum = parseInt(taskFilter, 10);
    return myTasks.filter(t => (t.Status ?? t.status ?? 1) === statusNum);
  }, [myTasks, taskFilter]);

  const taskTabCounts = useMemo(() => {
    const inProgress = myTasks.filter(t => (t.Status ?? t.status ?? 1) === TASK_STATUS.InProgress).length;
    const underReview = myTasks.filter(t => (t.Status ?? t.status ?? 1) === TASK_STATUS.UnderReview).length;
    const completed = myTasks.filter(t => (t.Status ?? t.status ?? 1) === TASK_STATUS.Completed).length;
    const rejected = myTasks.filter(t => (t.Status ?? t.status ?? 1) === TASK_STATUS.Rejected).length;
    return { inProgress, underReview, completed, rejected, all: myTasks.length };
  }, [myTasks]);

  // Sıralanmış görevler (filtrelenmiş listeyi sırala)
  const sortedTasks = useMemo(() => {
    if (!taskSortColumn) return filteredTasksForDashboard;
    const sorted = [...filteredTasksForDashboard];
    sorted.sort((a, b) => {
      let aValue, bValue;
      switch (taskSortColumn) {
        case 'foyNo':
          aValue = a.FoyNo ?? a.foyNo ?? 0;
          bValue = b.FoyNo ?? b.foyNo ?? 0;
          return taskSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'muvekkil':
          aValue = (a.Muvekkil ?? a.muvekkil ?? '').toLowerCase();
          bValue = (b.Muvekkil ?? b.muvekkil ?? '').toLowerCase();
          return taskSortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'description':
          aValue = (a.Description ?? a.description ?? '').toLowerCase();
          bValue = (b.Description ?? b.description ?? '').toLowerCase();
          return taskSortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'dueDate':
          // UTC+3 (Istanbul) timezone'a göre sıralama
          const parseTaskDate = (dateValue) => {
            if (!dateValue) return new Date(0);
            try {
              let dateObj;
              if (typeof dateValue === 'string') {
                if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d{3})?$/.test(dateValue)) {
                  const parts = dateValue.split('T');
                  const datePart = parts[0].split('-');
                  const timePart = (parts[1] || '00:00:00').split(':');
                  const year = parseInt(datePart[0], 10);
                  const month = parseInt(datePart[1], 10) - 1;
                  const day = parseInt(datePart[2], 10);
                  const hours = parseInt(timePart[0] || 0, 10);
                  const minutes = parseInt(timePart[1] || 0, 10);
                  const seconds = parseInt(timePart[2] || 0, 10);
                  dateObj = new Date(Date.UTC(year, month, day, hours, minutes, seconds));
                } else {
                  dateObj = new Date(dateValue);
                }
              } else {
                dateObj = new Date(dateValue);
              }
              // UTC+3'e çevir
              return new Date(dateObj.getTime() + (3 * 60 * 60 * 1000));
            } catch (e) {
              return new Date(0);
            }
          };
          aValue = parseTaskDate(a.DueDate ?? a.dueDate);
          bValue = parseTaskDate(b.DueDate ?? b.dueDate);
          return taskSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        default:
          return 0;
      }
    });
    return sorted;
  }, [filteredTasksForDashboard, taskSortColumn, taskSortDirection]);

  const handleNoteSort = (column) => {
    if (noteSortColumn === column) {
      setNoteSortDirection(noteSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setNoteSortColumn(column);
      setNoteSortDirection('asc');
    }
  };

  const sortedMyNotes = useMemo(() => {
    if (!myNotes.length) return myNotes;
    const sorted = [...myNotes];
    const dir = noteSortDirection === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      let va, vb;
      switch (noteSortColumn) {
        case 'baslik':
          va = (a.Title ?? a.title ?? '').toString();
          vb = (b.Title ?? b.title ?? '').toString();
          return dir * va.localeCompare(vb, 'tr');
        case 'icerik':
          va = (a.Content ?? a.content ?? '').toString();
          vb = (b.Content ?? b.content ?? '').toString();
          return dir * va.localeCompare(vb, 'tr');
        case 'tarih':
          va = a.NoteDate ?? a.noteDate ? new Date(a.NoteDate ?? a.noteDate).getTime() : 0;
          vb = b.NoteDate ?? b.noteDate ? new Date(b.NoteDate ?? b.noteDate).getTime() : 0;
          return dir * (va - vb);
        default:
          return 0;
      }
    });
    return sorted;
  }, [myNotes, noteSortColumn, noteSortDirection]);

  const handlePrintNotes = () => {
    if (sortedMyNotes.length === 0) {
      alert('Yazdırılacak not yok.');
      return;
    }
    const esc = (s) => {
      if (s == null || s === '') return '';
      const t = String(s);
      return t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/\n/g, '<br>');
    };
    const title = 'Notlarım';
    const thead = '<tr><th>Başlık</th><th>İçerik</th><th>Tarih</th></tr>';
    const rows = sortedMyNotes.map((note) => {
      const baslik = note.Title ?? note.title ?? '-';
      const icerik = note.Content ?? note.content ?? '-';
      const noteDate = note.NoteDate ?? note.noteDate;
      const tarihStr = noteDate ? formatDateForDisplay(noteDate) : '-';
      return `<tr>
        <td>${esc(baslik)}</td>
        <td>${esc(icerik)}</td>
        <td>${esc(tarihStr)}</td>
      </tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>
      body { font-family: Arial, sans-serif; font-size: 12px; padding: 16px; color: #333; }
      h1 { margin: 0 0 12px 0; font-size: 18px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #ddd; padding: 8px 10px; text-align: left; vertical-align: top; }
      th { background: #455a64; color: #fff; font-weight: 600; }
      tr:nth-child(even) { background: #f9f9f9; }
      td:nth-child(2) { white-space: pre-wrap; word-break: break-word; }
    </style></head><body><h1>${esc(title)}</h1><table><thead>${thead}</thead><tbody>${rows}</tbody></table></body></html>`;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Açılır pencere engellendi. Lütfen yazdırma için izin verin.');
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.onafterprint = () => printWindow.close();
    }, 250);
  };

  const openCompleteModal = (job) => {
    setCompleteModalJob(job);
    setCompleteComment('');
  };

  const handleCompleteWithComment = async () => {
    if (!completeModalJob) return;
    const jobId = completeModalJob.Id ?? completeModalJob.id;
    const caseFileId = completeModalJob.CaseFileId ?? completeModalJob.caseFileId;
    try {
      await jobAPI.update({
        JobId: jobId,
        IsCompleted: true,
        CompletedComment: completeComment.trim() || null
      });
      const allJobs = await jobAPI.getAll();
      setJobs(allJobs);
      setCompleteModalJob(null);
      setCompleteComment('');
      if (caseFileId) {
        window.dispatchEvent(new CustomEvent('jobUpdated', { detail: { caseFileId } }));
      }
      alert('İş tamamlandı. Onaylamak için Onayla demeniz gerekir.');
    } catch (error) {
      console.error('İş güncellenirken hata:', error);
      alert('İş güncellenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      alert('Lütfen red nedeni girin!');
      return;
    }
    if (!showRejectModal) {
      return;
    }
    try {
      await jobAPI.reject(showRejectModal, rejectionReason, newDueDate && newDueDate.trim() ? toISODateOrNull(newDueDate.trim()) : null);
      // Reload jobs
      const allJobs = await jobAPI.getAll();
      setJobs(allJobs);
      setShowRejectModal(null);
      setRejectionReason('');
      setNewDueDate('');
      alert('İş talebi reddedildi!');
    } catch (error) {
      console.error('İş talebi reddedilirken hata:', error);
      alert('İş talebi reddedilirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  useEffect(() => {
    if (token) {
      loadDashboardData();
    }
  }, [token]);

  // Karşılama alanı için canlı saat (dakikada bir günceller)
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Custom event dinleyicisi - CaseFileDetail'den iş tamamlandığında jobs'ı yeniden yükle
  useEffect(() => {
    const handleJobUpdated = async () => {
      try {
        const allJobs = await jobAPI.getAll();
        setJobs(allJobs);
      } catch (error) {
        console.error('Error reloading jobs:', error);
      }
    };

    const handleTaskUpdated = async () => {
      try {
        const tasks = await taskAPI.getMyTasks();
        setMyTasks(tasks);
      } catch (err) {
        console.error('Error reloading tasks:', err);
      }
    };

    window.addEventListener('jobUpdated', handleJobUpdated);
    window.addEventListener('taskUpdated', handleTaskUpdated);

    return () => {
      window.removeEventListener('jobUpdated', handleJobUpdated);
      window.removeEventListener('taskUpdated', handleTaskUpdated);
    };
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // PERFORMANS: Bu istekler birbirinin sonucuna ihtiyac duymuyor.
      // Eskiden yedisi de sirayla beklendigi icin acilis suresi tum
      // isteklerin TOPLAMI kadardi; simdi hepsi ayni anda gidiyor ve
      // acilis en yavas istek kadar suruyor. allSettled kullaniliyor ki
      // bir istek basarisiz olsa da digerleri panele yansisin.
      const yoneticiMi = user?.Role === 'Admin' || user?.role === 'Admin';

      const [
        caseFilesRes,
        archivedRes,
        tasksRes,
        usersRes,
        notesRes,
        jobsRes,
        vekaletRes,
      ] = await Promise.allSettled([
        caseFileAPI.getAll(),
        archiveRequestAPI.getArchivedFiles(),
        taskAPI.getMyTasks(),
        yoneticiMi ? userAPI.getAll() : Promise.resolve(null),
        noteAPI.getMyNotes(),
        jobAPI.getAll(),
        vekaletAPI.getAll(),
      ]);

      // Get active files (excludes archived)
      let caseFiles = [];
      if (caseFilesRes.status === 'fulfilled') {
        caseFiles = caseFilesRes.value || [];
        setCaseFiles(caseFiles); // Store for job form dropdown
      } else {
        console.error('Error fetching case files:', caseFilesRes.reason);
      }

      // Get archived files separately to fix the count
      let archivedFiles = [];
      if (archivedRes.status === 'fulfilled') {
        archivedFiles = archivedRes.value || [];
      } else {
        console.error('Error fetching archived files:', archivedRes.reason);
        // Fallback: count from caseFiles if API fails
        archivedFiles = caseFiles.filter(f => {
          const status = f.Status ?? f.status;
          return status === 3; // Archived = 3
        });
      }

      // Calculate statistics
      // Arşivde olmayan tüm dosyaları aktif dosya olarak kabul ediyoruz
      const totalFiles = caseFiles.length + archivedFiles.length;

      setStats({
        totalFiles: totalFiles,
        activeFiles: caseFiles.length,
        archivedFiles: archivedFiles.length
      });

      // Get my tasks (assigned to me)
      if (tasksRes.status !== 'fulfilled') {
        console.error('Error fetching my tasks:', tasksRes.reason);
      }
      setMyTasks(tasksRes.status === 'fulfilled' ? (tasksRes.value || []) : []);

      // Get users for admin (task edit form)
      if (yoneticiMi) {
        if (usersRes.status === 'fulfilled') {
          setUsers(usersRes.value || []);
        } else {
          console.error('Error fetching users:', usersRes.reason);
        }
      }

      // Get my notes
      if (notesRes.status !== 'fulfilled') {
        console.error('Error fetching my notes:', notesRes.reason);
      }
      setMyNotes(notesRes.status === 'fulfilled' ? (notesRes.value || []) : []);

      // Get all jobs (iş listesi)
      if (jobsRes.status !== 'fulfilled') {
        console.error('Error fetching jobs:', jobsRes.reason);
      }
      setJobs(jobsRes.status === 'fulfilled' ? (jobsRes.value || []) : []);

      // Vekaletler (bitiş tarihi yaklaşanlar kartı için)
      if (vekaletRes.status !== 'fulfilled') {
        console.error('Error fetching vekaletler:', vekaletRes.reason);
      }
      setVekaletler(
        vekaletRes.status === 'fulfilled' && Array.isArray(vekaletRes.value)
          ? vekaletRes.value
          : []
      );
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Set default stats on error
      setStats({
        totalFiles: 0,
        activeFiles: 0,
        archivedFiles: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // Tahkim: günü geçenler onaylanana kadar üst listede kırmızı kalır; kutuya basınca geçmişe iner
  const { upcomingFiles, pastFiles } = useMemo(() => {
    const todayKey = getTodayDateString();
    const limitKey = addDaysToDateKey(todayKey, 15);
    const up = [];
    const past = [];
    (caseFiles || []).forEach((f) => {
      const t = f.TahkimBasvuruTarihi ?? f.tahkimBasvuruTarihi;
      const dateKey = formatDateToLocalString(t);
      if (!dateKey) return;
      const fileId = f.Id ?? f.id;
      const markKey = `tahkim_${fileId}`;
      const done = Object.prototype.hasOwnProperty.call(localMarks, markKey)
        ? !!localMarks[markKey]
        : !!(f.TahkimBasvuruYapildi ?? f.tahkimBasvuruYapildi);
      if (dateKey < todayKey) {
        if (done) past.push(f);
        else up.push(f);
      } else if (dateKey <= limitKey) up.push(f);
    });
    up.sort((a, b) => (formatDateToLocalString(a.TahkimBasvuruTarihi ?? a.tahkimBasvuruTarihi) || '').localeCompare(formatDateToLocalString(b.TahkimBasvuruTarihi ?? b.tahkimBasvuruTarihi) || ''));
    past.sort((a, b) => (formatDateToLocalString(b.TahkimBasvuruTarihi ?? b.tahkimBasvuruTarihi) || '').localeCompare(formatDateToLocalString(a.TahkimBasvuruTarihi ?? a.tahkimBasvuruTarihi) || ''));
    return { upcomingFiles: up, pastFiles: past };
  }, [caseFiles, localMarks]);

  // Zamanaşımı: 3 ay içinde dolacaklar + günü geçenler
  const { zamanAsimiApproaching, zamanAsimiPast } = useMemo(() => {
    const todayKey = getTodayDateString();
    const limitKey = addMonthsToDateKey(todayKey, 3);
    const up = [];
    const past = [];
    (caseFiles || []).forEach((f) => {
      const t = f.ZamanAsimiTarihi ?? f.zamanAsimiTarihi;
      const dateKey = formatDateToLocalString(t);
      if (!dateKey) return;
      if (dateKey < todayKey) past.push(f);
      else if (dateKey <= limitKey) up.push(f);
    });
    up.sort((a, b) => (formatDateToLocalString(a.ZamanAsimiTarihi ?? a.zamanAsimiTarihi) || '').localeCompare(formatDateToLocalString(b.ZamanAsimiTarihi ?? b.zamanAsimiTarihi) || ''));
    past.sort((a, b) => (formatDateToLocalString(b.ZamanAsimiTarihi ?? b.zamanAsimiTarihi) || '').localeCompare(formatDateToLocalString(a.ZamanAsimiTarihi ?? a.zamanAsimiTarihi) || ''));
    return { zamanAsimiApproaching: up, zamanAsimiPast: past };
  }, [caseFiles]);

  // Vekaletname: bitişine 1 ay kalanlar + süresi geçmişler
  const { vekaletExpiring, vekaletPast } = useMemo(() => {
    const todayKey = getTodayDateString();
    const limitKey = addMonthsToDateKey(todayKey, 1);
    const fileById = new Map((caseFiles || []).map((f) => [f.Id ?? f.id, f]));
    const up = [];
    const past = [];
    (vekaletler || []).forEach((v) => {
      const turu = v.VekaletTuru ?? v.vekaletTuru;
      if (turu !== 'Süreli') return;
      const t = v.VekaletBitisTarihi ?? v.vekaletBitisTarihi;
      const dateKey = formatDateToLocalString(t);
      if (!dateKey) return;
      const caseFileId = v.CaseFileId ?? v.caseFileId;
      const file = fileById.get(caseFileId) || {};
      const row = {
        Id: caseFileId,
        id: caseFileId,
        Muvekkil: file.Muvekkil ?? file.muvekkil ?? v.Muvekkil ?? v.muvekkil ?? 'Bilinmiyor',
        muvekkil: file.Muvekkil ?? file.muvekkil,
        FoyNo: file.FoyNo ?? file.foyNo ?? v.FoyNo ?? v.foyNo,
        VekaletBitisTarihi: t,
        vekaletBitisTarihi: t,
      };
      if (dateKey < todayKey) past.push(row);
      else if (dateKey <= limitKey) up.push(row);
    });
    up.sort((a, b) => (formatDateToLocalString(a.VekaletBitisTarihi) || '').localeCompare(formatDateToLocalString(b.VekaletBitisTarihi) || ''));
    past.sort((a, b) => (formatDateToLocalString(b.VekaletBitisTarihi) || '').localeCompare(formatDateToLocalString(a.VekaletBitisTarihi) || ''));
    return { vekaletExpiring: up, vekaletPast: past };
  }, [vekaletler, caseFiles]);

  // Duruşma tarihi: yaklaşanlar (15 gün) + günü geçenler
  const { upcomingHearings, pastHearings } = useMemo(() => {
    const todayKey = getTodayDateString();
    const limitKey = addDaysToDateKey(todayKey, 15);
    const up = [];
    const past = [];
    (caseFiles || []).forEach((f) => {
      const t = f.DurusmaTarihi ?? f.durusmaTarihi;
      const dateKey = formatDateToLocalString(t);
      if (!dateKey) return;
      if (dateKey < todayKey) past.push(f);
      else if (dateKey <= limitKey) up.push(f);
    });
    up.sort((a, b) => (formatDateToLocalString(a.DurusmaTarihi ?? a.durusmaTarihi) || '').localeCompare(formatDateToLocalString(b.DurusmaTarihi ?? b.durusmaTarihi) || ''));
    past.sort((a, b) => (formatDateToLocalString(b.DurusmaTarihi ?? b.durusmaTarihi) || '').localeCompare(formatDateToLocalString(a.DurusmaTarihi ?? a.durusmaTarihi) || ''));
    return { upcomingHearings: up, pastHearings: past };
  }, [caseFiles]);

  // Sunucudaki (CaseFile) işaret değeri -> tüm kullanıcılarda ortak
  const getServerDoneValue = (file, type) => type === 'tahkim'
    ? !!(file.TahkimBasvuruYapildi ?? file.tahkimBasvuruYapildi)
    : !!(file.DurusmaGirildi ?? file.durusmaGirildi);

  // Görünen işaret durumu: kullanıcı bu öğeye dokunduysa yerel değer önceliklidir
  // (endpoint henüz yayınlanmamış olsa bile tik kaybolmaz), aksi halde sunucu değeri.
  const isDone = (file, type) => {
    const fileId = file.Id ?? file.id;
    const key = `${type}_${fileId}`;
    if (Object.prototype.hasOwnProperty.call(localMarks, key)) return !!localMarks[key];
    return getServerDoneValue(file, type);
  };

  const isMarking = (type, fileId) => !!markingKeys[`${type}_${fileId}`];

  const applyMarkToState = (fileId, type, value) => {
    setCaseFiles((prev) => (prev || []).map((f) => {
      if ((f.Id ?? f.id) !== fileId) return f;
      return type === 'tahkim'
        ? { ...f, TahkimBasvuruYapildi: value, tahkimBasvuruYapildi: value }
        : { ...f, DurusmaGirildi: value, durusmaGirildi: value };
    }));
  };

  const toggleDone = async (file, type) => {
    const fileId = file.Id ?? file.id;
    const key = `${type}_${fileId}`;
    if (markingKeys[key]) return; // çift tıklamayı engelle
    const current = isDone(file, type);
    const next = !current;

    // İyimser güncelleme: hem yerel yedeğe yaz (kalıcı) hem de dosya durumuna
    setLocalMarks((prev) => { const n = { ...prev, [key]: next }; persistLocalMarks(n); return n; });
    applyMarkToState(fileId, type, next);
    setMarkingKeys((p) => ({ ...p, [key]: true }));

    try {
      await caseFileAPI.setDurumIsaret(fileId, type, next);
      // Başarılı: sunucu artık yetkili, yerel geçersiz kılmayı kaldır
      setLocalMarks((prev) => { const n = { ...prev }; delete n[key]; persistLocalMarks(n); return n; });
    } catch (e) {
      // Sunucu erişilemedi (ör. endpoint henüz yayınlanmadı): yerel yedek korunur,
      // böylece tik ekranda kalır ve sayfa yenilense bile kaybolmaz.
      console.warn('İşaret sunucuya kaydedilemedi, yerel olarak saklandı:', e?.message || e);
    } finally {
      setMarkingKeys((p) => {
        const n = { ...p };
        delete n[key];
        return n;
      });
    }
  };

  const scrollToCard = (ref) => {
    const el = ref.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('dashboard-card-highlight');
    setTimeout(() => { if (el) el.classList.remove('dashboard-card-highlight'); }, 1800);
  };

  const renderCaseDateItem = (file, type, past = false) => {
    const muvekkil = file.Muvekkil ?? file.muvekkil ?? 'Bilinmiyor';
    const fileId = file.Id ?? file.id;
    let dateVal;
    if (type === 'tahkim') dateVal = file.TahkimBasvuruTarihi ?? file.tahkimBasvuruTarihi;
    else if (type === 'durusma') dateVal = file.DurusmaTarihi ?? file.durusmaTarihi;
    else if (type === 'zamanAsimi') dateVal = file.ZamanAsimiTarihi ?? file.zamanAsimiTarihi;
    else if (type === 'vekalet') dateVal = file.VekaletBitisTarihi ?? file.vekaletBitisTarihi;
    else dateVal = null;
    const dateStr = type === 'durusma' ? formatDateForDisplay(dateVal) : formatDateOnly(dateVal);
    const mahkeme = type === 'durusma' ? (file.MahkemaBilgisi ?? file.mahkemaBilgisi ?? '') : '';
    const showCheck = type === 'tahkim' || type === 'durusma';
    const done = showCheck ? isDone(file, type) : false;
    const marking = showCheck ? isMarking(type, fileId) : false;
    const doneLabel = type === 'tahkim' ? 'Başvuru yapıldı' : 'Duruşmaya girildi';
    const foyNo = file.FoyNo ?? file.foyNo;
    return (
      <div
        key={`${type}_${fileId}`}
        className={`upcoming-case-item ${done ? 'done' : ''} ${past ? 'past' : ''} ${marking ? 'marking' : ''}`}
      >
        {showCheck && (
          <button
            type="button"
            className={`upcoming-check ${done ? 'checked' : ''}`}
            disabled={marking}
            title={done ? 'İşareti kaldır' : (type === 'tahkim' ? 'Tahkim başvurusu yapıldı olarak işaretle' : 'Duruşmaya girildi olarak işaretle')}
            onClick={(e) => { e.stopPropagation(); toggleDone(file, type); }}
          >
            {marking ? <span className="upcoming-check-spinner" /> : (done ? '✓' : '')}
          </button>
        )}
        <div className="upcoming-case-info" onClick={() => navigate(`/case-files/${fileId}`)}>
          <div className="case-muvekkil">{foyNo ? `${foyNo} — ${muvekkil}` : muvekkil}</div>
          <div className="case-date">
            {dateStr}
            {mahkeme && <span className="case-mahkeme"> - {mahkeme}</span>}
          </div>
        </div>
        {done && <span className="upcoming-done-badge">{doneLabel}</span>}
      </div>
    );
  };

  const heroGreetingName = (() => {
    if (!user) return '';
    const name = user.Name ?? user.name ?? '';
    const surname = user.Surname ?? user.surname ?? '';
    const full = `${name}${surname ? ' ' + surname : ''}`.trim();
    const base = full || (user.Username ?? user.UserName ?? '');
    const title = user.Title ?? user.title ?? '';
    const isAvukat = title === 'Avukat' || title === 'Lawyer' || title === 'lawyer';
    return isAvukat && base ? `Av. ${base}` : base;
  })();
  const heroGreeting = (() => {
    const h = now.getHours();
    if (h < 6) return 'İyi geceler';
    if (h < 12) return 'Günaydın';
    if (h < 18) return 'İyi günler';
    return 'İyi akşamlar';
  })();
  const heroDateStr = now.toLocaleDateString('tr-TR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const heroTimeStr = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="dashboard">
      {/* Karşılama + Navbar tek bütün blok */}
      <div className="dashboard-hero-wrap">
      {/* Karşılama / Marka Alanı */}
      <div className="dashboard-hero">
        <div className="dashboard-hero-main">
          <div className="dashboard-hero-logo"><img src="/assets/logo.png" alt="Öztürk Hukuk ve Danışmanlık" /></div>
          <div className="dashboard-hero-text">
            <h1 className="dashboard-hero-title">Öztürk Hukuk ve Danışmanlık</h1>
            <p className="dashboard-hero-subtitle">Hukuk &amp; Danışmanlık Yönetim Sistemi</p>
            <div className="dashboard-hero-meta">
              <span className="dashboard-hero-greeting">
                {heroGreeting}{heroGreetingName ? `, ${heroGreetingName}` : ''}
              </span>
              <span className="dashboard-hero-dot">•</span>
              <span className="dashboard-hero-datetime">
                {heroDateStr} · {heroTimeStr}
              </span>
            </div>
          </div>
        </div>
          <div className="dashboard-hero-stats">
          <div className="dashboard-hero-stat" onClick={() => scrollToCard(hearingsRef)} role="button" title="Yaklaşan duruşmaları göster">
            <span className="dashboard-hero-stat-num">{upcomingHearings.length}</span>
            <span className="dashboard-hero-stat-lbl">Yaklaşan Duruşma</span>
          </div>
          <div className="dashboard-hero-stat" onClick={() => scrollToCard(arbitrationRef)} role="button" title="Yaklaşan tahkim başvurularını göster">
            <span className="dashboard-hero-stat-num">{upcomingFiles.length}</span>
            <span className="dashboard-hero-stat-lbl">Yaklaşan Tahkim</span>
          </div>
        </div>
      </div>

      {/* Navbar - karşılama bölümünün hemen altında (yalnızca ana sayfada) */}
      <div className="dashboard-navbar-slot">
        <Navbar />
      </div>
      </div>

      {/* Main Content Grid */}
      <div
        className="dashboard-main-grid"
        ref={mainSplitRef}
        style={{ gridTemplateColumns: `${mainLeftPct}fr 12px ${100 - mainLeftPct}fr` }}
      >
        {/* Sekmeli Panel - Bana Atanan Görevler ve İş Listesi */}
        <div className="dashboard-card tasks-jobs-card">
          <div className="dashboard-panel-tabs-row">
            <div className="dashboard-panel-tabs">
              <button
                type="button"
                className={`dashboard-panel-tab ${activeTab === 'jobs' ? 'active' : ''}`}
                onClick={() => setActiveTab('jobs')}
              >
                İş Listesi
                <span className="dashboard-panel-tab-badge">{jobs.filter(j => !(j.IsCompleted ?? j.isCompleted ?? false)).length}</span>
              </button>
              <button
                type="button"
                className={`dashboard-panel-tab ${activeTab === 'tasks' ? 'active' : ''}`}
                onClick={() => setActiveTab('tasks')}
              >
                Bana Atanan Görevler
                <span className="dashboard-panel-tab-badge">{myTasks.filter(t => (t.Status ?? t.status ?? 1) === TASK_STATUS.InProgress).length}</span>
              </button>
            </div>
            {activeTab === 'jobs' && (
              <button
                type="button"
                className="btn btn-primary btn-sm dashboard-action-btn"
                onClick={() => {
                  setEditingJob(null);
                  setJobFormData({ Description: '', DueDate: '', CaseFileId: null });
                  setShowJobForm(true);
                }}
              >
                + Yeni İş
              </button>
            )}
          </div>

          {/* İçerik Alanı */}
          <div className="card-content" style={{ padding: '20px', paddingBottom: '35px' }}>
            {/* Bana Atanan Görevler Sekmesi */}
            {activeTab === 'tasks' && (
              <>
                {loading ? (
                  <div className="empty-state">
                    <p>Yükleniyor...</p>
                  </div>
                ) : myTasks.length === 0 ? (
                  <div className="empty-state">
                    <p>Henüz size atanan görev bulunmamaktadır</p>
                    <p className="empty-subtitle">
                      {isAdmin() ? (
                        <>Görev atamak ve onay bekleyen görevleri yönetmek için <Link to="/tasks" style={{ color: '#667eea', fontWeight: '600', textDecoration: 'underline' }}>Görevler sayfasına gidin</Link></>
                      ) : (
                        'Admin tarafından size görev atandığında burada görünecektir'
                      )}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Bana Atanan Görevler - Filtre sekmeleri */}
                    <div className="dashboard-filter-tabs">
                      <button type="button" className={`dashboard-filter-tab ${taskFilter === String(TASK_STATUS.InProgress) ? 'active' : ''}`} onClick={() => setTaskFilter(String(TASK_STATUS.InProgress))}>
                        {TASK_STATUS_LABELS[TASK_STATUS.InProgress]} ({taskTabCounts.inProgress})
                      </button>
                      <button type="button" className={`dashboard-filter-tab ${taskFilter === String(TASK_STATUS.UnderReview) ? 'active' : ''}`} onClick={() => setTaskFilter(String(TASK_STATUS.UnderReview))}>
                        {TASK_STATUS_LABELS[TASK_STATUS.UnderReview]} ({taskTabCounts.underReview})
                      </button>
                      <button type="button" className={`dashboard-filter-tab ${taskFilter === String(TASK_STATUS.Completed) ? 'active' : ''}`} onClick={() => setTaskFilter(String(TASK_STATUS.Completed))}>
                        {TASK_STATUS_LABELS[TASK_STATUS.Completed]} ({taskTabCounts.completed})
                      </button>
                      <button type="button" className={`dashboard-filter-tab ${taskFilter === String(TASK_STATUS.Rejected) ? 'active' : ''}`} onClick={() => setTaskFilter(String(TASK_STATUS.Rejected))}>
                        {TASK_STATUS_LABELS[TASK_STATUS.Rejected]} ({taskTabCounts.rejected})
                      </button>
                      <button type="button" className={`dashboard-filter-tab ${taskFilter === 'all' ? 'active' : ''}`} onClick={() => setTaskFilter('all')}>
                        Tümü ({taskTabCounts.all})
                      </button>
                    </div>

                    {filteredTasksForDashboard.length === 0 ? (
                      <div className="empty-state">
                        <p>Bu kategoride görev bulunamadı</p>
                      </div>
                    ) : (
                  <div className="dashboard-panel-table-wrap tasks-table-container">
                    <table className="dashboard-panel-table tasks-table">
                      <thead>
                        <tr>
                          <th onClick={() => handleTaskSort('foyNo')}>Foy No <span style={{ marginLeft: '4px', fontSize: '9px', opacity: 0.9 }}>{taskSortColumn === 'foyNo' ? (taskSortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span></th>
                          <th onClick={() => handleTaskSort('muvekkil')}>Müvekkil <span style={{ marginLeft: '4px', fontSize: '9px', opacity: 0.9 }}>{taskSortColumn === 'muvekkil' ? (taskSortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span></th>
                          <th onClick={() => handleTaskSort('description')}>Görev Açıklaması <span style={{ marginLeft: '4px', fontSize: '9px', opacity: 0.9 }}>{taskSortColumn === 'description' ? (taskSortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span></th>
                          <th onClick={() => handleTaskSort('dueDate')}>Son Tarih <span style={{ marginLeft: '4px', fontSize: '9px', opacity: 0.9 }}>{taskSortColumn === 'dueDate' ? (taskSortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span></th>
                          <th>Durum</th>
                          <th style={{ minWidth: '140px' }}>İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedTasks.map((task) => {
                          const foyNo = task.FoyNo ?? task.foyNo;
                          const muvekkil = task.Muvekkil ?? task.muvekkil;
                          const description = task.Description ?? task.description;
                          const taskId = task.Id ?? task.id;
                          const caseFileId = task.CaseFileId ?? task.caseFileId;
                          const dueDate = task.DueDate ?? task.dueDate;
                          const status = parseInt(task.Status ?? task.status ?? 1);
                          
                          const formatDate = (date) => {
                            if (!date) return '-';
                            return formatDateForDisplay(date);
                          };
                          
                          return (
                            <tr key={taskId} onClick={() => navigate(`/tasks`)} style={{ cursor: 'pointer' }}>
                              <td className="dashboard-cell-foy">{foyNo || '-'}</td>
                              <td>{muvekkil || '-'}</td>
                              <td>{description || '-'}</td>
                              <td>{formatDate(dueDate)}</td>
                              <td>
                                <span className="dashboard-status-badge" style={{
                                  backgroundColor: status === 1 ? '#fff8e1' : status === 2 ? '#e3f2fd' : status === 3 ? '#e8f5e9' : '#ffebee',
                                  color: status === 1 ? '#f57f17' : status === 2 ? '#1565c0' : status === 3 ? '#2e7d32' : '#c62828'
                                }}>
                                  {TASK_STATUS_LABELS[status] || 'Bilinmiyor'}
                                </span>
                              </td>
                              <td onClick={(e) => e.stopPropagation()}>
                                {status === TASK_STATUS.InProgress && (
                                  <ActionIcon
                                    type="complete"
                                    title={isAdmin() ? 'Tamamlandı Yap' : 'Kontrole Gönder'}
                                    onClick={(e) => { e.stopPropagation(); setEditingTaskMode('complete'); setEditingTask(task); }}
                                  />
                                )}
                                {isAdmin() && (
                                  <>
                                    <ActionIcon type="edit" title="Düzenle" onClick={(e) => { e.stopPropagation(); setEditingTaskMode('edit'); setEditingTask(task); }} />
                                    <ActionIcon type="delete" title="Sil" onClick={(e) => { e.stopPropagation(); handleDeleteTask(task); }} />
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* İş Listesi Sekmesi */}
            {activeTab === 'jobs' && (
              <>
                {loading ? (
                  <div className="empty-state">
                    <p>Yükleniyor...</p>
                  </div>
                ) : jobs.length === 0 && YENI_TASARIM ? (
                  <BosDurum simge="liste" kucuk baslik="Bugün iş yok" metin='Yeni iş eklemek için "Yeni İş" düğmesine dokunun.' />
                ) : jobs.length === 0 ? (
                  <div className="empty-state">
                    <p>Henüz iş eklenmemiş</p>
                    <p className="empty-subtitle">Yeni iş eklemek için "Yeni İş" butonuna tıklayın</p>
                  </div>
                ) : (
                  <>
                    {/* İş Listesi Filtre Sekmeleri */}
                    <div className="dashboard-filter-tabs">
                      <button type="button" className={`dashboard-filter-tab ${jobFilter === 'pending' ? 'active' : ''}`} onClick={() => setJobFilter('pending')}>
                        Yapılacaklar ({jobs.filter(j => !(j.IsCompleted ?? j.isCompleted ?? false)).length})
                      </button>
                      <button type="button" className={`dashboard-filter-tab ${jobFilter === 'underReview' ? 'active' : ''}`} onClick={() => setJobFilter('underReview')}>
                        Kontrole Gidenler ({jobs.filter(j => { const c = j.IsCompleted ?? j.isCompleted ?? false; const s = j.Status ?? j.status ?? 0; return c && (s === 0 || s === 1); }).length})
                      </button>
                      <button type="button" className={`dashboard-filter-tab ${jobFilter === 'approved' ? 'active' : ''}`} onClick={() => setJobFilter('approved')}>
                        Onaylananlar ({jobs.filter(j => (j.Status ?? j.status ?? 0) === 2).length})
                      </button>
                      <button type="button" className={`dashboard-filter-tab ${jobFilter === 'rejected' ? 'active' : ''}`} onClick={() => setJobFilter('rejected')}>
                        Reddedilenler ({jobs.filter(j => (j.Status ?? j.status ?? 0) === 3).length})
                      </button>
                      <button type="button" className={`dashboard-filter-tab ${jobFilter === 'all' ? 'active' : ''}`} onClick={() => setJobFilter('all')}>
                        Tümü ({jobs.length})
                      </button>
                    </div>

                    {filteredAndSortedJobs.length === 0 ? (
                      <div className="empty-state">
                        <p>Bu kategoride iş bulunamadı</p>
                        <p className="empty-subtitle">Yeni iş eklemek için "Yeni İş" butonuna tıklayın</p>
                      </div>
                    ) : (
                      <div className="dashboard-panel-table-wrap jobs-table-container">
                        <table className="dashboard-panel-table jobs-table">
                      <thead>
                        <tr>
                          <th onClick={() => handleSort('foyNo')}>Foy No <span style={{ marginLeft: '4px', fontSize: '9px', opacity: 0.9 }}>{sortColumn === 'foyNo' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span></th>
                          <th onClick={() => handleSort('muvekkil')}>Müvekkil <span style={{ marginLeft: '4px', fontSize: '9px', opacity: 0.9 }}>{sortColumn === 'muvekkil' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span></th>
                          <th onClick={() => handleSort('description')}>İş Açıklaması <span style={{ marginLeft: '4px', fontSize: '9px', opacity: 0.9 }}>{sortColumn === 'description' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span></th>
                          <th onClick={() => handleSort('dueDate')}>Son Tarih <span style={{ marginLeft: '4px', fontSize: '9px', opacity: 0.9 }}>{sortColumn === 'dueDate' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span></th>
                          <th style={{ minWidth: '140px' }}>İşlem</th>
                          <th onClick={() => handleSort('createdBy')}>Oluşturan <span style={{ marginLeft: '4px', fontSize: '9px', opacity: 0.9 }}>{sortColumn === 'createdBy' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span></th>
                          <th onClick={() => handleSort('createdDate')}>Oluşturma Tarihi <span style={{ marginLeft: '4px', fontSize: '9px', opacity: 0.9 }}>{sortColumn === 'createdDate' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span></th>
                          <th onClick={() => handleSort('completedBy')}>İşi Yapan <span style={{ marginLeft: '4px', fontSize: '9px', opacity: 0.9 }}>{sortColumn === 'completedBy' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span></th>
                          <th onClick={() => handleSort('completedDate')}>Tamamlanma Tarihi <span style={{ marginLeft: '4px', fontSize: '9px', opacity: 0.9 }}>{sortColumn === 'completedDate' ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAndSortedJobs.map((job) => {
                          const jobId = job.Id ?? job.id;
                          const description = job.Description ?? job.description;
                          const createdDate = job.CreatedDate ?? job.createdDate;
                          const dueDate = job.DueDate ?? job.dueDate;
                          const createdBy = `${job.CreatedByUserName ?? job.createdByUserName ?? ''} ${job.CreatedByUserSurname ?? job.createdByUserSurname ?? ''}`.trim();
                          const isCompleted = job.IsCompleted ?? job.isCompleted ?? false;
                          const caseFileFoyNo = job.CaseFileFoyNo ?? job.caseFileFoyNo;
                          const caseFileMuvekkil = job.CaseFileMuvekkil ?? job.caseFileMuvekkil;
                          const caseFileId = job.CaseFileId ?? job.caseFileId;
                          
                          const formatDate = (date) => {
                            if (!date) return '-';
                            // UTC+3 (Istanbul) timezone için formatDateForDisplay kullan
                            return formatDateForDisplay(date);
                          };
                          
                          // isOverdue kontrolü: UTC+3 (Istanbul) timezone'a göre
                          let isOverdue = false;
                          if (dueDate && !isCompleted) {
                            try {
                              let dueDateObj;
                              if (typeof dueDate === 'string') {
                                if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d{3})?$/.test(dueDate)) {
                                  const parts = dueDate.split('T');
                                  const datePart = parts[0].split('-');
                                  const timePart = (parts[1] || '00:00:00').split(':');
                                  const year = parseInt(datePart[0], 10);
                                  const month = parseInt(datePart[1], 10) - 1;
                                  const day = parseInt(datePart[2], 10);
                                  const hours = parseInt(timePart[0] || 0, 10);
                                  const minutes = parseInt(timePart[1] || 0, 10);
                                  const seconds = parseInt(timePart[2] || 0, 10);
                                  dueDateObj = new Date(Date.UTC(year, month, day, hours, minutes, seconds));
                                } else {
                                  dueDateObj = new Date(dueDate);
                                }
                              } else {
                                dueDateObj = new Date(dueDate);
                              }
                              
                              // UTC+3'e çevir
                              const dueDateIstanbul = new Date(dueDateObj.getTime() + (3 * 60 * 60 * 1000));
                              
                              // Bugünün tarihini UTC+3 olarak al
                              const now = new Date();
                              const nowIstanbul = new Date(now.getTime() + (3 * 60 * 60 * 1000));
                              
                              // Sadece tarih karşılaştırması yap
                              const dueDateOnly = new Date(dueDateIstanbul.getUTCFullYear(), dueDateIstanbul.getUTCMonth(), dueDateIstanbul.getUTCDate());
                              const nowDateOnly = new Date(nowIstanbul.getUTCFullYear(), nowIstanbul.getUTCMonth(), nowIstanbul.getUTCDate());
                              
                              isOverdue = dueDateOnly < nowDateOnly;
                            } catch (e) {
                              isOverdue = false;
                            }
                          }
                          const completedBy = `${job.CompletedByUserName ?? job.completedByUserName ?? ''} ${job.CompletedByUserSurname ?? job.completedByUserSurname ?? ''}`.trim();
                          const completedDate = job.CompletedDate ?? job.completedDate;
                          const status = job.Status ?? job.status ?? 0; // 0: Pending, 1: UnderReview, 2: Approved, 3: Rejected
                          const rejectionReason = job.RejectionReason ?? job.rejectionReason;
                          const currentUserId = user?.Id ?? user?.id;
                          const completedByUserId = job.CompletedByUserId ?? job.completedByUserId;
                          
                          // Sadece işi yapan kişi "Kontrol Ediliyor" butonunu görebilir
                          const canRequestReview = isCompleted && status === 0 && completedByUserId === currentUserId;
                          
                          const handleRequestReview = async (e) => {
                            e.stopPropagation();
                            if (!await onayla('Bu işi kontrol için talep etmek istediğinizden emin misiniz?')) {
                              return;
                            }
                            try {
                              await jobAPI.requestReview(jobId);
                              // Reload jobs
                              const allJobs = await jobAPI.getAll();
                              setJobs(allJobs);
                              alert('İş kontrol için talep edildi!');
                            } catch (error) {
                              console.error('İş talebi oluşturulurken hata:', error);
                              alert('İş talebi oluşturulurken hata oluştu: ' + (error.response?.data?.message || error.message));
                            }
                          };

                          const handleApprove = async (e) => {
                            e.stopPropagation();
                            if (!await onayla('Bu iş talebini onaylamak istediğinize emin misiniz?')) {
                              return;
                            }
                            try {
                              await jobAPI.approve(jobId);
                              // Reload jobs
                              const allJobs = await jobAPI.getAll();
                              setJobs(allJobs);
                              alert('İş talebi onaylandı!');
                            } catch (error) {
                              console.error('İş talebi onaylanırken hata:', error);
                              alert('İş talebi onaylanırken hata oluştu: ' + (error.response?.data?.message || error.message));
                            }
                          };

                          const handleReject = async (e) => {
                            e.stopPropagation();
                            setShowRejectModal(jobId);
                          };

                          const handleEdit = async (e) => {
                            e.stopPropagation();
                            // Yetki kontrolü: Admin veya işi oluşturan kullanıcı
                            const createdByUserId = job.CreatedByUserId ?? job.createdByUserId;
                            if (!isAdmin() && createdByUserId !== currentUserId) {
                              alert('Bu işi sadece oluşturan kullanıcı veya admin düzenleyebilir.');
                              return;
                            }
                            setEditingJob(job);
                            setShowJobForm(true);
                          };

                          const handleDelete = async (e) => {
                            e.stopPropagation();
                            // Yetki kontrolü: Admin veya işi oluşturan kullanıcı
                            const createdByUserId = job.CreatedByUserId ?? job.createdByUserId;
                            if (!isAdmin() && createdByUserId !== currentUserId) {
                              alert('Bu işi sadece oluşturan kullanıcı veya admin silebilir.');
                              return;
                            }
                            if (!await onayla('Bu işi silmek istediğinize emin misiniz?')) {
                              return;
                            }
                            try {
                              await jobAPI.delete(jobId);
                              // Reload jobs
                              const allJobs = await jobAPI.getAll();
                              setJobs(allJobs);
                              // Custom event gönder - CaseFileDetail sayfası dinleyebilir
                              if (caseFileId) {
                                window.dispatchEvent(new CustomEvent('jobUpdated', { 
                                  detail: { caseFileId: caseFileId } 
                                }));
                              }
                              alert('İş başarıyla silindi!');
                            } catch (error) {
                              console.error('İş silinirken hata:', error);
                              alert('İş silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
                            }
                          };
                          
                          return (
                            <tr key={jobId} onClick={() => setSelectedJob(job)} style={{ cursor: 'pointer' }}>
                              <td className="dashboard-cell-foy" onClick={(e) => e.stopPropagation()}>
                                {caseFileFoyNo && caseFileId ? (
                                  <Link 
                                    to={`/case-files/${caseFileId}`}
                                    style={{ color: '#667eea', textDecoration: 'underline', cursor: 'pointer' }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {caseFileFoyNo}
                                  </Link>
                                ) : (
                                  caseFileFoyNo || '-'
                                )}
                              </td>
                              <td
                                style={{ padding: '1px 10px', fontSize: '11px', color: '#666', maxWidth: '160px' }}
                                title={caseFileMuvekkil || '-'}
                              >
                                <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {caseFileMuvekkil || '-'}
                                </span>
                              </td>
                              <td
                                style={{
                                  padding: '1px 10px',
                                  fontSize: '11px',
                                  color: '#333',
                                  maxWidth: '200px'
                                }}
                                title={[description || '-', status === 3 && !isCompleted && rejectionReason && `Red Nedeni: ${rejectionReason}`].filter(Boolean).join('\n')}
                              >
                                <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {description || '-'}
                                </span>
                                {status === 3 && !isCompleted && rejectionReason && (
                                  <div style={{ marginTop: '5px', padding: '5px', backgroundColor: '#ffebee', borderRadius: '4px', fontSize: '10px', color: '#c62828' }}>
                                    <strong>Red Nedeni:</strong> {rejectionReason}
                                  </div>
                                )}
                              </td>
                              <td
                                className={isOverdue ? 'job-due-overdue' : ''}
                                style={{ padding: '1px 10px', fontSize: '11px', color: isOverdue ? '#dc2626' : '#666', fontWeight: isOverdue ? '700' : 'normal', whiteSpace: 'nowrap' }}
                              >
                                {formatJobDueDateForDisplay(dueDate)}
                              </td>
                              <td style={{ padding: '1px 10px', textAlign: 'center', whiteSpace: 'nowrap', display: 'flex', flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'nowrap' }}>
                                  {(() => {
                                    const createdByUserId = job.CreatedByUserId ?? job.createdByUserId;
                                    const canEditDelete = isAdmin() || createdByUserId === currentUserId;
                                    
                                    return (
                                      <>
                                        {canEditDelete && (
                                          <>
                                            <ActionIcon type="edit" title="Düzenle" onClick={handleEdit} />
                                            <ActionIcon type="delete" title="Sil" onClick={handleDelete} />
                                          </>
                                        )}
                                        {!isCompleted && (
                                          <ActionIcon type="complete" title="Kontrole Gönder" onClick={(e) => { e.stopPropagation(); openCompleteModal(job); }} />
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                                {isCompleted && status === 0 && (
                                  <div style={{ display: 'inline-flex', gap: '5px', alignItems: 'center', flexWrap: 'nowrap' }}>
                                    <span style={{ color: '#4caf50', fontWeight: '600', fontSize: '10px' }}>Tamamlandı</span>
                                    {canRequestReview && (
                                      <button
                                        className="btn btn-warning btn-sm"
                                        onClick={handleRequestReview}
                                        style={{
                                          padding: '4px 8px',
                                          fontSize: '10px',
                                          minWidth: 'auto',
                                          backgroundColor: '#ff9800',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: 'pointer'
                                        }}
                                        title="Kontrol talep et"
                                      >
                                        Kontrol Ediliyor
                                      </button>
                                    )}
                                  </div>
                                )}
                                {isCompleted && status === 1 && (
                                  <div style={{ display: 'inline-flex', gap: '5px', alignItems: 'center', flexWrap: 'nowrap' }}>
                                    <span style={{ color: '#ff9800', fontWeight: '600', fontSize: '10px' }}>Kontrol Ediliyor</span>
                                    {isAdmin() && (
                                      <>
                                        <ActionIcon type="approve" title="Onayla" onClick={handleApprove} />
                                        <ActionIcon type="reject" title="Reddet" onClick={handleReject} />
                                      </>
                                    )}
                                  </div>
                                )}
                                {isCompleted && status === 2 && (
                                  <span style={{ color: '#4caf50', fontWeight: '600', fontSize: '10px' }}>Onaylandı</span>
                                )}
                                {!isCompleted && status === 3 && (
                                  <span style={{ color: '#f44336', fontWeight: '600', fontSize: '10px' }}>Reddedildi</span>
                                )}
                              </td>
                              <td style={{ padding: '1px 10px', fontSize: '11px', color: '#666' }}>{createdBy || '-'}</td>
                              <td style={{ padding: '1px 10px', fontSize: '11px', color: '#666' }}>{formatDate(createdDate)}</td>
                              <td style={{ padding: '1px 10px', fontSize: '11px', color: isCompleted ? '#4caf50' : '#666', fontWeight: isCompleted ? '600' : 'normal' }}>{completedBy || '-'}</td>
                              <td style={{ padding: '1px 10px', fontSize: '11px', color: isCompleted ? '#4caf50' : '#666' }}>{formatDate(completedDate)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div
          className="dashboard-split-handle"
          onMouseDown={beginMainSplit}
          onDoubleClick={() => setMainLeftPct(50)}
          title="Sürükleyerek genişliği ayarlayın (çift tıkla sıfırla)"
          role="separator"
          aria-orientation="vertical"
        >
          <span className="dashboard-split-grip" />
        </div>

        {/* Right Panel - Notlarım */}
        <div className="dashboard-card notes-card" style={{ position: 'relative' }}>
          <div className="card-header">
            <h2><span className="dash-title-text">Notlarım</span></h2>
            <div className="card-header-actions">
              <button
                type="button"
                className="btn btn-sm dashboard-notes-print-btn"
                onClick={handlePrintNotes}
                title="Notları yazdır"
                disabled={myNotes.length === 0}
              >
                Yazdır
              </button>
              <button
                className="btn btn-primary btn-sm dashboard-action-btn"
                onClick={() => {
                  setEditingNote(null);
                  setShowNoteForm(true);
                }}
              >
                + Yeni Not
              </button>
            </div>
          </div>
          <div className="card-content">
            {myNotes.length > 0 ? (
              <div className="notes-table-container dashboard-panel-table-wrap" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100% - 20px)' }}>
                <table className="dashboard-panel-table notes-table">
                  <thead>
                    <tr>
                      <th
                        onClick={() => handleNoteSort('baslik')}
                        style={{ cursor: 'pointer' }}
                        title="Sıralamak için tıklayın"
                      >
                        Başlık <span style={{ marginLeft: '4px', fontSize: '9px', opacity: noteSortColumn === 'baslik' ? 1 : 0.7 }}>{noteSortColumn === 'baslik' ? (noteSortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span>
                      </th>
                      <th
                        onClick={() => handleNoteSort('icerik')}
                        style={{ cursor: 'pointer' }}
                        title="Sıralamak için tıklayın"
                      >
                        İçerik <span style={{ marginLeft: '4px', fontSize: '9px', opacity: noteSortColumn === 'icerik' ? 1 : 0.7 }}>{noteSortColumn === 'icerik' ? (noteSortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span>
                      </th>
                      <th
                        onClick={() => handleNoteSort('tarih')}
                        style={{ cursor: 'pointer' }}
                        title="Sıralamak için tıklayın"
                      >
                        Tarih <span style={{ marginLeft: '4px', fontSize: '9px', opacity: noteSortColumn === 'tarih' ? 1 : 0.7 }}>{noteSortColumn === 'tarih' ? (noteSortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMyNotes.map((note) => {
                      const noteDate = note.NoteDate || note.noteDate;
                      const formatDate = (date) => {
                        if (!date) return '-';
                        return formatDateForDisplay(date);
                      };
                      
                      return (
                        <tr
                          key={note.Id || note.id}
                          onClick={() => {
                            setEditingNote(note);
                            setShowNoteForm(true);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="dashboard-cell-foy">
                            {note.Title || note.title || '-'}
                          </td>
                          <td className="notes-content-cell">
                            {(note.Content || note.content || '').substring(0, 50)}
                            {(note.Content || note.content || '').length > 50 ? '...' : ''}
                          </td>
                          <td>
                            {formatDate(noteDate)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="card-text">Henüz notunuz yok. Yeni not oluşturmak için "Yeni Not" butonuna tıklayın.</p>
            )}
          </div>
        </div>
      </div>

      {/* Statistics Cards - 2x2 */}
      <div className="bottom-stats-grid" ref={bottomSplitRef}>
        <div
          className="bottom-stats-row"
          style={{ gridTemplateColumns: `${bottomLeftPct}fr 12px ${100 - bottomLeftPct}fr` }}
        >
          <div className="dashboard-card upcoming-card" ref={arbitrationRef}>
            <div className="card-header">
              <h3><span className="dash-title-text">Tahkim Başvuru Tarihi Yaklaşanlar</span></h3>
              <span className="badge">{upcomingFiles.length}</span>
            </div>
            <div className="card-content">
              <div className="upcoming-card-subrow">
                <p className="card-subtitle">Yaklaşan Tarihler (15 Gün)</p>
                {pastFiles.length > 0 && (
                  <button
                    type="button"
                    className="dashboard-past-toggle"
                    onClick={() => setShowPastFiles((s) => !s)}
                  >
                    {showPastFiles ? 'Geçmişi Gizle' : `Geçmişi Göster (${pastFiles.length})`}
                  </button>
                )}
              </div>
              {(upcomingFiles.length > 0 || (showPastFiles && pastFiles.length > 0)) ? (
                <div className="upcoming-case-dates-list">
                  {upcomingFiles.map((file) => {
                    const dateKey = formatDateToLocalString(file.TahkimBasvuruTarihi ?? file.tahkimBasvuruTarihi);
                    const overdue = !!(dateKey && dateKey < getTodayDateString());
                    return renderCaseDateItem(file, 'tahkim', overdue);
                  })}
                  {showPastFiles && pastFiles.length > 0 && (
                    <>
                      <div className="upcoming-past-divider">Günü Geçen Tahkim Başvuruları</div>
                      {pastFiles.map((file) => renderCaseDateItem(file, 'tahkim', true))}
                    </>
                  )}
                </div>
              ) : (
                YENI_TASARIM
                  ? <BosDurum simge="takvim" kucuk baslik="Yaklaşan tahkim yok" />
                  : <p className="card-text">Yaklaşan tahkim başvuru tarihi bulunmamaktadır.</p>
              )}
            </div>
          </div>
          <div
            className="dashboard-split-handle"
            onMouseDown={beginBottomSplit}
            onDoubleClick={() => setBottomLeftPct(50)}
            title="Sürükleyerek genişliği ayarlayın (çift tıkla sıfırla)"
            role="separator"
            aria-orientation="vertical"
          >
            <span className="dashboard-split-grip" />
          </div>
          <div className="dashboard-card case-dates-card" ref={hearingsRef}>
            <div className="card-header">
              <h3><span className="dash-title-text">Duruşma Tarihi Yaklaşanlar</span></h3>
              <span className="badge">{upcomingHearings.length}</span>
            </div>
            <div className="card-content">
              <div className="upcoming-card-subrow">
                <p className="card-subtitle">Yaklaşan Tarihler (15 Gün)</p>
                {pastHearings.length > 0 && (
                  <button
                    type="button"
                    className="dashboard-past-toggle"
                    onClick={() => setShowPastHearings((s) => !s)}
                  >
                    {showPastHearings ? 'Geçmişi Gizle' : `Geçmişi Göster (${pastHearings.length})`}
                  </button>
                )}
              </div>
              {(upcomingHearings.length > 0 || (showPastHearings && pastHearings.length > 0)) ? (
                <div className="upcoming-case-dates-list">
                  {upcomingHearings.map((file) => renderCaseDateItem(file, 'durusma', false))}
                  {showPastHearings && pastHearings.length > 0 && (
                    <>
                      <div className="upcoming-past-divider">Günü Geçen Duruşmalar</div>
                      {pastHearings.map((file) => renderCaseDateItem(file, 'durusma', true))}
                    </>
                  )}
                </div>
              ) : (
                YENI_TASARIM
                  ? <BosDurum simge="takvim" kucuk baslik="Yaklaşan duruşma yok" />
                  : <p className="card-text">Yaklaşan duruşma tarihi bulunmamaktadır.</p>
              )}
            </div>
          </div>
        </div>

        <div
          className="bottom-stats-row"
          style={{ gridTemplateColumns: `${bottomLeftPct}fr 12px ${100 - bottomLeftPct}fr` }}
        >
          <div className="dashboard-card upcoming-card">
            <div className="card-header">
              <h3><span className="dash-title-text">Zamanaşımına 3 Ay Kalanlar</span></h3>
              <span className="badge">{zamanAsimiApproaching.length}</span>
            </div>
            <div className="card-content">
              <div className="upcoming-card-subrow">
                <p className="card-subtitle">Zamanaşımı tarihi 3 ay içinde dolacak dosyalar</p>
                {zamanAsimiPast.length > 0 && (
                  <button
                    type="button"
                    className="dashboard-past-toggle"
                    onClick={() => setShowPastZamanAsimi((s) => !s)}
                  >
                    {showPastZamanAsimi ? 'Geçmişi Gizle' : `Geçmişi Göster (${zamanAsimiPast.length})`}
                  </button>
                )}
              </div>
              {(zamanAsimiApproaching.length > 0 || (showPastZamanAsimi && zamanAsimiPast.length > 0)) ? (
                <div className="upcoming-case-dates-list">
                  {zamanAsimiApproaching.map((file) => renderCaseDateItem(file, 'zamanAsimi', false))}
                  {showPastZamanAsimi && zamanAsimiPast.length > 0 && (
                    <>
                      <div className="upcoming-past-divider">Günü Geçmiş Zamanaşımı Tarihleri</div>
                      {zamanAsimiPast.map((file) => renderCaseDateItem(file, 'zamanAsimi', true))}
                    </>
                  )}
                </div>
              ) : (
                <p className="card-text">Zamanaşımına 3 ay kalan dosya bulunmamaktadır.</p>
              )}
            </div>
          </div>
          <div
            className="dashboard-split-handle"
            onMouseDown={beginBottomSplit}
            onDoubleClick={() => setBottomLeftPct(50)}
            title="Sürükleyerek genişliği ayarlayın (çift tıkla sıfırla)"
            role="separator"
            aria-orientation="vertical"
          >
            <span className="dashboard-split-grip" />
          </div>
          <div className="dashboard-card case-dates-card">
            <div className="card-header">
              <h3><span className="dash-title-text">Vekaletname Süresi 1 Ay Kalanlar</span></h3>
              <span className="badge">{vekaletExpiring.length}</span>
            </div>
            <div className="card-content">
              <div className="upcoming-card-subrow">
                <p className="card-subtitle">Süreli vekaletname bitişine 1 ay kalan dosyalar</p>
                {vekaletPast.length > 0 && (
                  <button
                    type="button"
                    className="dashboard-past-toggle"
                    onClick={() => setShowPastVekalet((s) => !s)}
                  >
                    {showPastVekalet ? 'Geçmişi Gizle' : `Geçmişi Göster (${vekaletPast.length})`}
                  </button>
                )}
              </div>
              {(vekaletExpiring.length > 0 || (showPastVekalet && vekaletPast.length > 0)) ? (
                <div className="upcoming-case-dates-list">
                  {vekaletExpiring.map((file) => renderCaseDateItem(file, 'vekalet', false))}
                  {showPastVekalet && vekaletPast.length > 0 && (
                    <>
                      <div className="upcoming-past-divider">Süresi Geçmiş Vekaletnameler</div>
                      {vekaletPast.map((file) => renderCaseDateItem(file, 'vekalet', true))}
                    </>
                  )}
                </div>
              ) : (
                <p className="card-text">Vekaletname süresi 1 ay içinde dolacak dosya bulunmamaktadır.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-file-stats">
        <button type="button" className="dashboard-file-stat" onClick={() => navigate('/case-files')} title="Dosyalar">
          <span className="dashboard-file-stat-num">{stats.totalFiles}</span>
          <span className="dashboard-file-stat-lbl">Toplam Dosya</span>
        </button>
        <button type="button" className="dashboard-file-stat" onClick={() => navigate('/case-files')} title="Aktif dosyalar">
          <span className="dashboard-file-stat-num">{stats.activeFiles}</span>
          <span className="dashboard-file-stat-lbl">Aktif Dosya</span>
        </button>
      </div>

      {/* Job Form Modal */}
      {showJobForm && (
        <JobForm
          job={editingJob}
          onSubmit={async (jobData) => {
            try {
              if (editingJob) {
                // Güncelleme
                await jobAPI.update({
                  JobId: editingJob.Id || editingJob.id,
                  Description: jobData.Description,
                  DueDate: jobData.DueDate || null
                });
                alert('İş başarıyla güncellendi!');
              } else {
                // Yeni iş oluşturma
                await jobAPI.create({
                  Description: jobData.Description,
                  DueDate: jobData.DueDate || null,
                  CaseFileId: jobFormData.CaseFileId || null
                });
                alert('İş başarıyla eklendi!');
              }
              setShowJobForm(false);
              setEditingJob(null);
              setJobFormData({ Description: '', DueDate: '', CaseFileId: null });
              // Reload jobs
              const allJobs = await jobAPI.getAll();
              setJobs(allJobs);
              // Custom event gönder
              if (editingJob && (editingJob.CaseFileId || editingJob.caseFileId)) {
                window.dispatchEvent(new CustomEvent('jobUpdated', { 
                  detail: { caseFileId: editingJob.CaseFileId || editingJob.caseFileId } 
                }));
              }
            } catch (error) {
              console.error('İş kaydedilirken hata:', error);
              alert('İş kaydedilirken hata oluştu: ' + (error.response?.data?.message || error.message));
            }
          }}
          onCancel={() => {
            setShowJobForm(false);
            setEditingJob(null);
            setJobFormData({ Description: '', DueDate: '', CaseFileId: null });
          }}
          onAssignedAsTask={async () => {
            const allJobs = await jobAPI.getAll();
            setJobs(allJobs);
            try {
              const tasks = await taskAPI.getMyTasks();
              setMyTasks(tasks || []);
            } catch (_) { /* ignore */ }
          }}
          onDelete={async () => {
            if (!editingJob) return;
            try {
              await jobAPI.delete(editingJob.Id || editingJob.id);
              alert('İş başarıyla silindi!');
              setShowJobForm(false);
              setEditingJob(null);
              // Reload jobs
              const allJobs = await jobAPI.getAll();
              setJobs(allJobs);
              // Custom event gönder
              if (editingJob.CaseFileId || editingJob.caseFileId) {
                window.dispatchEvent(new CustomEvent('jobUpdated', { 
                  detail: { caseFileId: editingJob.CaseFileId || editingJob.caseFileId } 
                }));
              }
            } catch (error) {
              console.error('İş silinirken hata:', error);
              alert('İş silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
            }
          }}
        />
      )}

      {/* İş Detay Modal - Anasayfa iş listesinden satıra tıklanınca */}
      {selectedJob && (
        <div className="modal-overlay" onClick={() => setSelectedJob(null)}>
          <div className="modal-content dashboard-job-detail-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#2c3e50' }}>İş Detayı</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => setSelectedJob(null)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}
                aria-label="Kapat"
              >
                ×
              </button>
            </div>
            <div className="dashboard-job-detail-body">
              {(() => {
                const j = selectedJob;
                const jobId = j.Id ?? j.id;
                const caseFileId = j.CaseFileId ?? j.caseFileId;
                const foyNo = j.CaseFileFoyNo ?? j.caseFileFoyNo;
                const muvekkil = j.CaseFileMuvekkil ?? j.caseFileMuvekkil;
                const description = j.Description ?? j.description;
                const dueDate = j.DueDate ?? j.dueDate;
                const isCompleted = j.IsCompleted ?? j.isCompleted ?? false;
                const status = j.Status ?? j.status ?? 0;
                const createdBy = `${j.CreatedByUserName ?? j.createdByUserName ?? ''} ${j.CreatedByUserSurname ?? j.createdByUserSurname ?? ''}`.trim();
                const completedBy = `${j.CompletedByUserName ?? j.completedByUserName ?? ''} ${j.CompletedByUserSurname ?? j.completedByUserSurname ?? ''}`.trim();
                const createdDate = j.CreatedDate ?? j.createdDate;
                const completedDate = j.CompletedDate ?? j.completedDate;
                const completedComment = j.CompletedComment ?? j.completedComment;
                const currentUserId = user?.Id ?? user?.id;
                const createdByUserId = j.CreatedByUserId ?? j.createdByUserId;
                const completedByUserId = j.CompletedByUserId ?? j.completedByUserId;
                const canEditDelete = isAdmin() || createdByUserId === currentUserId;
                const canComplete = !isCompleted;
                const canRequestReview = isCompleted && status === 0 && completedByUserId === currentUserId;
                const canApproveReject = isAdmin() && isCompleted && (status === 0 || status === 1);
                let isOverdue = false;
                if (dueDate && !isCompleted) {
                  try {
                    const d = typeof dueDate === 'string' ? new Date(dueDate) : new Date(dueDate);
                    isOverdue = d < new Date();
                  } catch (_) {}
                }
                const formatDate = (date) => (!date ? '-' : formatDateForDisplay(date));
                return (
                  <>
                    <div className="dashboard-job-detail-row">
                      <span className="dashboard-job-detail-label">Föy No</span>
                      <span className="dashboard-job-detail-value">
                        {foyNo != null && caseFileId ? (
                          <Link to={`/case-files/${caseFileId}`} onClick={() => setSelectedJob(null)} style={{ color: '#667eea', fontWeight: 600 }}>{foyNo}</Link>
                        ) : (foyNo ?? '-')}
                      </span>
                    </div>
                    <div className="dashboard-job-detail-row">
                      <span className="dashboard-job-detail-label">Müvekkil</span>
                      <span className="dashboard-job-detail-value">{muvekkil ?? '-'}</span>
                    </div>
                    <div className="dashboard-job-detail-row">
                      <span className="dashboard-job-detail-label">Açıklama</span>
                      <span className="dashboard-job-detail-value" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{description ?? '-'}</span>
                    </div>
                    <div className="dashboard-job-detail-row">
                      <span className="dashboard-job-detail-label">Son Tarih</span>
                      <span className="dashboard-job-detail-value" style={{ color: isOverdue ? '#c0392b' : undefined, fontWeight: isOverdue ? 600 : undefined }}>{formatJobDueDateForDisplay(dueDate)}</span>
                    </div>
                    <div className="dashboard-job-detail-row">
                      <span className="dashboard-job-detail-label">Oluşturan</span>
                      <span className="dashboard-job-detail-value">{createdBy || '-'}</span>
                    </div>
                    {createdDate && (
                      <div className="dashboard-job-detail-row">
                        <span className="dashboard-job-detail-label">Oluşturma Tarihi</span>
                        <span className="dashboard-job-detail-value">{formatDate(createdDate)}</span>
                      </div>
                    )}
                    <div className="dashboard-job-detail-row">
                      <span className="dashboard-job-detail-label">İşi Yapan</span>
                      <span className="dashboard-job-detail-value">{completedBy || '-'}</span>
                    </div>
                    {completedDate && (
                      <div className="dashboard-job-detail-row">
                        <span className="dashboard-job-detail-label">Tamamlanma Tarihi</span>
                        <span className="dashboard-job-detail-value">{formatDate(completedDate)}</span>
                      </div>
                    )}
                    {completedComment && (
                      <div className="dashboard-job-detail-row">
                        <span className="dashboard-job-detail-label">Tamamlanma Yorumu</span>
                        <span className="dashboard-job-detail-value" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{completedComment}</span>
                      </div>
                    )}
                    <div className="dashboard-job-detail-row">
                      <span className="dashboard-job-detail-label">Durum</span>
                      <span className="dashboard-job-detail-value">
                        <span style={{
                          display: 'inline-block',
                          padding: '3px 8px',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          background: isCompleted ? '#d4edda' : '#fff3cd',
                          color: isCompleted ? '#155724' : '#856404'
                        }}>
                          {getJobStatusLabel(j)}
                        </span>
                      </span>
                    </div>
                    <div className="dashboard-job-detail-actions">
                      {canComplete && (
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => { openCompleteModal(j); setSelectedJob(null); }} style={{ backgroundColor: '#28a745' }}>Tamamla</button>
                      )}
                      {canRequestReview && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={async () => {
                          if (!await onayla('Bu işi kontrol için talep etmek istediğinizden emin misiniz?')) return;
                          try {
                            await jobAPI.requestReview(jobId);
                            const allJobs = await jobAPI.getAll();
                            setJobs(allJobs);
                            setSelectedJob(null);
                            alert('İş kontrol için talep edildi!');
                          } catch (err) {
                            alert('Hata: ' + (err.response?.data?.message || err.message));
                          }
                        }}>Talep Et</button>
                      )}
                      {canApproveReject && (
                        <>
                          <button type="button" className="btn btn-primary btn-sm" onClick={async () => {
                            if (!await onayla('Bu iş talebini onaylamak istediğinize emin misiniz?')) return;
                            try {
                              await jobAPI.approve(jobId);
                              const allJobs = await jobAPI.getAll();
                              setJobs(allJobs);
                              setSelectedJob(null);
                              alert('İş talebi onaylandı!');
                            } catch (err) {
                              alert('Hata: ' + (err.response?.data?.message || err.message));
                            }
                          }} style={{ backgroundColor: '#28a745' }}>Onayla</button>
                          <button type="button" className="btn btn-danger btn-sm" onClick={() => { setShowRejectModal(jobId); }}>Reddet</button>
                        </>
                      )}
                      {canEditDelete && (
                        <>
                          <button type="button" className="btn btn-sm dashboard-job-detail-btn-duzenle" onClick={() => { setEditingJob(j); setShowJobForm(true); setSelectedJob(null); }}>Düzenle</button>
                          <button type="button" className="btn btn-danger btn-sm" onClick={async () => {
                            if (!await onayla('Bu işi silmek istediğinize emin misiniz?')) return;
                            try {
                              await jobAPI.delete(jobId);
                              const allJobs = await jobAPI.getAll();
                              setJobs(allJobs);
                              setSelectedJob(null);
                              if (caseFileId) window.dispatchEvent(new CustomEvent('jobUpdated', { detail: { caseFileId } }));
                              alert('İş silindi.');
                            } catch (err) {
                              alert('Hata: ' + (err.response?.data?.message || err.message));
                            }
                          }}>Sil</button>
                        </>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Not Form Modal */}
      {showNoteForm && (
        <NoteForm
          note={editingNote}
          onSubmit={async (noteData) => {
            setLoadingNote(true);
            try {
              if (editingNote) {
                await noteAPI.update(editingNote.Id || editingNote.id, noteData);
                alert('Not başarıyla güncellendi');
              } else {
                await noteAPI.create(noteData);
                alert('Not başarıyla oluşturuldu');
              }
              setShowNoteForm(false);
              setEditingNote(null);
              // Reload notes
              const notes = await noteAPI.getMyNotes();
              setMyNotes(notes);
            } catch (error) {
              console.error('Not kaydedilirken hata:', error);
              alert('Not kaydedilirken hata oluştu: ' + (error.response?.data?.message || error.message));
            } finally {
              setLoadingNote(false);
            }
          }}
          onDelete={async () => {
            if (!editingNote) return;
            setLoadingNote(true);
            try {
              await noteAPI.delete(editingNote.Id || editingNote.id);
              alert('Not başarıyla silindi');
              setShowNoteForm(false);
              setEditingNote(null);
              // Reload notes
              const notes = await noteAPI.getMyNotes();
              setMyNotes(notes);
            } catch (error) {
              console.error('Not silinirken hata:', error);
              alert('Not silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
            } finally {
              setLoadingNote(false);
            }
          }}
          onCancel={() => {
            setShowNoteForm(false);
            setEditingNote(null);
          }}
          loading={loadingNote}
        />
      )}

      {editingTask && (
        <TaskForm
          task={editingTask}
          caseFiles={caseFiles}
          users={users}
          onSubmit={handleTaskUpdate}
          onCancel={() => { setEditingTask(null); setEditingTaskMode('complete'); }}
          loading={loadingTask}
          editMode={editingTaskMode === 'edit'}
        />
      )}

      {/* İşi Tamamla Modal */}
      {completeModalJob && (
        <div className="modal-overlay" onClick={() => { setCompleteModalJob(null); setCompleteComment(''); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#2c3e50' }}>İşi Tamamla</h3>
              <button
                className="modal-close"
                onClick={() => { setCompleteModalJob(null); setCompleteComment(''); }}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}
              >
                ×
              </button>
            </div>
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#555' }}>Yorum (Opsiyonel)</label>
              <textarea
                value={completeComment}
                onChange={(e) => setCompleteComment(e.target.value)}
                rows="4"
                placeholder="Tamamlanırken eklemek istediğiniz yorum..."
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>
            <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button className="btn btn-secondary" onClick={() => { setCompleteModalJob(null); setCompleteComment(''); }}>İptal</button>
              <button className="btn btn-primary" onClick={handleCompleteWithComment} style={{ backgroundColor: '#28a745' }}>Tamamla</button>
            </div>
          </div>
        </div>
      )}

      {/* Reddetme Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => {
          setShowRejectModal(null);
          setRejectionReason('');
          setNewDueDate('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#2c3e50' }}>İş Talebini Reddet</h3>
              <button 
                className="modal-close" 
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectionReason('');
                  setNewDueDate('');
                }}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}
              >
                ×
              </button>
            </div>
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#555' }}>Red Nedeni *</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows="4"
                placeholder="Red nedeni girin..."
                required
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#555' }}>Yeni Son Tarih (Opsiyonel)</label>
              <input
                type="date"
                value={newDueDate}
                onChange={(e) => setNewDueDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '12px' }}>
                İş reddedildikten sonra yeni bir son tarih belirleyebilirsiniz.
              </small>
            </div>
            <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                className="btn btn-primary"
                onClick={handleRejectConfirm}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Reddet
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectionReason('');
                  setNewDueDate('');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;